var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
import { useContext, useMemo } from 'react';
import { generatePath, matchPath, useLocation } from 'react-router-dom';
import { useAsyncEffect, useMemoOnce } from '@hilma/tools';
import { TransformObjectContext, LanguageContext, TranslatedI18nContext, TranslateContext, IsI18nRouterContext } from '../context';
import { Language } from '../types';
import { isLanguage } from '../functions';
export var createTranslatedI18nHook = function () {
    var useTranslatedI18n = function () { return useContext(TranslatedI18nContext); };
    return useTranslatedI18n;
};
export var createTranslateHook = function () {
    var useTranslate = function () { return useContext(TranslateContext); };
    return useTranslate;
};
export var createI18nHook = function () {
    var useTranslatedI18n = createTranslatedI18nHook();
    function useI18n(selector) {
        var translatedI18n = useTranslatedI18n();
        var isSelector = useMemoOnce(function () { return !!selector; });
        var i18n = useMemo(function () {
            return isSelector ? selector(translatedI18n) : translatedI18n;
        }, [translatedI18n, isSelector]);
        return i18n;
    }
    return useI18n;
};
export var createTranslateTextsHook = function () {
    var useTranslate = createTranslateHook();
    var useTranslateTexts = function (texts) {
        var translate = useTranslate();
        var translatedTexts = useMemo(function () {
            return texts.map(translate);
        }, [translate]);
        return translatedTexts;
    };
    return useTranslateTexts;
};
export var useBaseTranslatedI18n = createTranslatedI18nHook();
export var useBaseI18n = createI18nHook();
export var useBaseTranslate = createTranslateHook();
export var useBaseTranslateTexts = createTranslateTextsHook();
export var useLanguage = function () { return useContext(LanguageContext); };
export var useTransformObject = function () { return useContext(TransformObjectContext); };
export var useIsI18nRouter = function () { return useContext(IsI18nRouterContext); };
export var useLanguageEffect = function (effect, deps) {
    if (deps === void 0) { deps = []; }
    var language = useLanguage();
    useAsyncEffect(function () {
        return effect(language);
    }, __spreadArray([language], deps));
};
export var usePathLanguage = function () {
    var location = useLocation();
    var pathLanguage = useMemo(function () {
        var match = matchPath(location.pathname, {
            path: '/:language'
        });
        return isLanguage(match === null || match === void 0 ? void 0 : match.params.language) ? match === null || match === void 0 ? void 0 : match.params.language : null;
    }, [location.pathname]);
    return pathLanguage;
};
export var useDirection = function () {
    var language = useLanguage();
    var direction = useMemo(function () {
        if (language === "en")
            return 'ltr';
        else if (language === "he")
            return 'rtl';
        else
            throw new Error("Language not valid");
    }, [language]);
    return direction;
};
export function useTransform(object) {
    var transformObject = useTransformObject();
    var transformed = useMemo(function () {
        if (!object)
            return object;
        if (Array.isArray(object))
            return object.map(transformObject);
        return transformObject(object);
    }, [object, transformObject]);
    return transformed;
}
export var useNoLanguagePathname = function () {
    var location = useLocation();
    var language = useLanguage();
    var noLanguagePathname = useMemo(function () {
        return location.pathname.split("/" + language)[1];
    }, [location.pathname, language]);
    return noLanguagePathname;
};
export var useSwitchedPath = function () {
    var language = useLanguage();
    var location = useLocation();
    var switchedPath = useMemo(function () {
        var _a = location.pathname.split('/'), rest = _a.slice(2);
        var newPath = generatePath("/:langauge/" + rest.join('/'), {
            langauge: language === Language.En ? Language.He : Language.En
        });
        return newPath;
    }, [language, location.pathname]);
    return switchedPath;
};
//# sourceMappingURL=i18n-hooks.js.map